import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ArrowLeft, TrendingUp, ShoppingBag, DollarSign, Receipt, ChevronUp, ChevronDown } from 'lucide-react';
import { businessDashboardService, DailySalesRow } from '../../api/services/businessDashboardService';
import { MainLayout } from '../../components/layout/MainLayout';

/* ─── DARK MODE ────────────────────────────────────────────────────────── */

const useIsDark = () => {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
};

/* ─── CONSTANTS ────────────────────────────────────────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ─── FORMATTERS ───────────────────────────────────────────────────────── */

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtShort = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const fmtFull = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/* ─── SHARED COMPONENTS ────────────────────────────────────────────────── */

const YearSelect: React.FC<{ value: number; onChange: (y: number) => void; label: string }> = ({ value, onChange, label }) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{label}</span>
      <select
        value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};

const Card: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  controls?: React.ReactNode;
}> = ({ title, icon, children, controls }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-2 text-gray-800 dark:text-white font-semibold text-sm">
        {icon}{title}
      </div>
      {controls && <div className="flex items-center gap-3">{controls}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Skeleton: React.FC<{ h?: string }> = ({ h = 'h-64' }) => (
  <div className={`${h} rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse`} />
);

const SalesTooltip: React.FC<any> = ({ active, payload, label, primaryYear, compareYear }) => {
  if (!active || !payload?.length) return null;
  const p1 = payload.find((p: any) => String(p.name) === String(primaryYear))?.value ?? 0;
  const p2 = payload.find((p: any) => String(p.name) === String(compareYear))?.value ?? 0;
  const diff = p1 - p2;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
      ))}
      {p2 > 0 && (
        <div className={`mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1 font-semibold
          ${diff >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
          {diff >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          {fmt(Math.abs(diff))} ({Math.abs((diff / p2) * 100).toFixed(1)}%)
        </div>
      )}
    </div>
  );
};

/* ─── PAGE ─────────────────────────────────────────────────────────────── */

const CustomerReport: React.FC = () => {
  const { customerId: encodedId } = useParams<{ customerId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = useIsDark();
  const tickColor = isDark ? '#ffffff' : '#6b7280';

  const customerId = encodedId ? decodeURIComponent(encodedId) : '';
  const stateCustomerName = (location.state as { customerName?: string } | null)?.customerName;

  const currentYear = new Date().getFullYear();
  const [year1, setYear1] = useState(currentYear);
  const [year2, setYear2] = useState(currentYear - 1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  const isFiltered = startDate !== '' && endDate !== '';

  const parsedStart = useMemo(() => {
    if (!startDate) return null;
    const [y, m, d] = startDate.split('-').map(Number);
    return { year: y, month: m, day: d };
  }, [startDate]);

  const parsedEnd = useMemo(() => {
    if (!endDate) return null;
    const [y, m, d] = endDate.split('-').map(Number);
    return { year: y, month: m, day: d };
  }, [endDate]);

  const effectiveFrom = parsedStart?.month ?? 1;
  const effectiveTo   = parsedEnd?.month   ?? 12;
  const fromDay       = parsedStart?.day   ?? 1;
  const toDay         = parsedEnd?.day     ?? 31;

  useEffect(() => {
    if (parsedStart) setYear1(parsedStart.year);
  }, [parsedStart]);

  const rangeLabel = isFiltered
    ? `${startDate} – ${endDate}`
    : (effectiveFrom === effectiveTo ? MONTHS[effectiveFrom - 1] : `${MONTHS[effectiveFrom - 1]}–${MONTHS[effectiveTo - 1]}`);

  /* ── Monthly report (summary + monthly sales + top products) ── */
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-report', customerId, year1, year2],
    queryFn: () => businessDashboardService.getCustomerReport(customerId, year1, year2),
    staleTime: 5 * 60 * 1000,
    enabled: !!customerId,
  });

  /* ── Daily sales (enabled only when month range is narrowed) ── */
  const { data: dailyData = [], isLoading: dailyLoading, isError: dailyError } = useQuery<DailySalesRow[]>({
    queryKey: ['customer-daily-sales', customerId, year1, year2, effectiveFrom, effectiveTo, fromDay, toDay],
    queryFn: () => businessDashboardService.getCustomerDailySales(customerId, year1, year2, effectiveFrom, effectiveTo, fromDay, toDay),
    staleTime: 5 * 60 * 1000,
    enabled: !!customerId && isFiltered,
  });

  const chartLoading = isFiltered ? dailyLoading : isLoading;
  const chartError   = isFiltered ? dailyError   : isError;

  const customerName = data?.customerName ?? stateCustomerName ?? customerId;

  /* ── KPI values (always full-year from summary) ── */
  const summary1 = data?.summary.find(r => r.Year === year1);
  const summary2 = data?.summary.find(r => r.Year === year2);

  const revenueChange = summary2?.TotalRevenue && summary2.TotalRevenue > 0
    ? (((summary1?.TotalRevenue ?? 0) - summary2.TotalRevenue) / summary2.TotalRevenue) * 100
    : null;
  const ordersChange = summary2?.TotalOrders && summary2.TotalOrders > 0
    ? (((summary1?.TotalOrders ?? 0) - summary2.TotalOrders) / summary2.TotalOrders) * 100
    : null;
  const avgChange = summary2?.AvgOrderValue && summary2.AvgOrderValue > 0
    ? (((summary1?.AvgOrderValue ?? 0) - summary2.AvgOrderValue) / summary2.AvgOrderValue) * 100
    : null;

  /* ── Monthly chart data (12 points) ── */
  const monthlyChartData = useMemo(() => {
    const byKey: Record<string, Record<number, number>> = {};
    for (const row of data?.monthlySales ?? []) {
      const k = String(row.Month);
      if (!byKey[k]) byKey[k] = {};
      byKey[k][row.Year] = row.Revenue;
    }
    return MONTHS.map((month, i) => ({
      label: month,
      monthIndex: i + 1,
      [year1]: byKey[String(i + 1)]?.[year1] ?? 0,
      [year2]: byKey[String(i + 1)]?.[year2] ?? 0,
    }));
  }, [data, year1, year2]);

  /* ── Daily chart data (one point per calendar day in range) ── */
  const dailyChartData = useMemo(() => {
    if (!isFiltered) return [];
    const lookup: Record<string, Record<number, number>> = {};
    for (const row of dailyData) {
      const k = `${row.Month}-${row.Day}`;
      if (!lookup[k]) lookup[k] = {};
      lookup[k][row.Year] = row.Revenue;
    }
    const rows: { label: string; monthIndex: number; dayIndex: number; [key: number]: number }[] = [];
    for (let m = effectiveFrom; m <= effectiveTo; m++) {
      const daysInMonth = new Date(year1, m, 0).getDate();
      const startDay = m === effectiveFrom ? fromDay : 1;
      const endDay   = m === effectiveTo   ? Math.min(toDay, daysInMonth) : daysInMonth;
      for (let d = startDay; d <= endDay; d++) {
        const k = `${m}-${d}`;
        rows.push({
          label: `${MONTHS[m - 1]} ${d}`,
          monthIndex: m,
          dayIndex: d,
          [year1]: lookup[k]?.[year1] ?? 0,
          [year2]: lookup[k]?.[year2] ?? 0,
        });
      }
    }
    return rows;
  }, [dailyData, effectiveFrom, effectiveTo, fromDay, toDay, year1, year2, isFiltered]);

  const dailyTicks = useMemo(() => {
    if (!dailyChartData.length) return [];
    const ticks: string[] = [];
    let lastTickIdx = -7;
    dailyChartData.forEach((r, i) => {
      const isFirst    = i === 0;
      const isMonthStart = r.dayIndex === 1;
      const isWeekly   = i - lastTickIdx >= 7;
      if (isFirst || isMonthStart || isWeekly) {
        ticks.push(r.label);
        lastTickIdx = i;
      }
    });
    return ticks;
  }, [dailyChartData]);

  const activeChartData = isFiltered ? dailyChartData : monthlyChartData;
  const dotRadius = isFiltered ? 0 : 3;

  /* ── Totals (respect month range) ── */
  const totals = useMemo(() => {
    const source = isFiltered ? dailyData : (data?.monthlySales ?? []);
    const inRange = (r: { Month: number; Day?: number }) =>
      isFiltered
        ? (r.Month > effectiveFrom || (r.Month === effectiveFrom && (r.Day ?? 1) >= fromDay)) &&
          (r.Month < effectiveTo   || (r.Month === effectiveTo   && (r.Day ?? 31) <= toDay))
        : r.Month >= effectiveFrom && r.Month <= effectiveTo;
    const t1 = source.filter(r => r.Year === year1 && inRange(r)).reduce((s, r) => s + r.Revenue, 0);
    const t2 = source.filter(r => r.Year === year2 && inRange(r)).reduce((s, r) => s + r.Revenue, 0);
    const diff = t2 > 0 ? ((t1 - t2) / t2) * 100 : 0;
    return { t1, t2, diff };
  }, [data, dailyData, year1, year2, effectiveFrom, effectiveTo, isFiltered]);

  /* ── KPI card ── */
  const KpiCard: React.FC<{
    label: string;
    value: string;
    sub: string;
    change: number | null;
    icon: React.ReactNode;
    accent: string;
  }> = ({ label, value, sub, change, icon, accent }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
        <span className={`p-2 rounded-lg ${accent}`}>{icon}</span>
      </div>
      {isLoading ? (
        <div className="h-8 w-32 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
      ) : (
        <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">{value}</p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400 dark:text-gray-500">{sub}</span>
        {change !== null && !isLoading && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full
            ${change >= 0
              ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
              : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20'
            }`}
          >
            {change >= 0 ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {Math.abs(change).toFixed(1)}% vs {year2}
          </span>
        )}
      </div>
    </div>
  );

  const yearControls = (
    <>
      <YearSelect value={year2} onChange={setYear2} label="Compare:" />
      <YearSelect value={year1} onChange={setYear1} label="Year:" />
    </>
  );

  return (
    <MainLayout>
      <div className="space-y-6 max-w-screen-2xl">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/admin/business-dashboard')}
            className="inline-flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 mb-3 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Business Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{customerName}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Customer sales report · {customerId}</p>
        </div>

        {/* Year selectors */}
        <div className="flex items-center gap-4">
          <YearSelect value={year1} onChange={setYear1} label="Year:" />
          <YearSelect value={year2} onChange={setYear2} label="Compare:" />
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard
            label="Total Revenue"
            value={isLoading ? '—' : fmt(summary1?.TotalRevenue ?? 0)}
            sub={`${year1} full year`}
            change={revenueChange}
            icon={<DollarSign size={16} className="text-indigo-600 dark:text-indigo-400" />}
            accent="bg-indigo-50 dark:bg-indigo-900/30"
          />
          <KpiCard
            label="Total Orders"
            value={isLoading ? '—' : (summary1?.TotalOrders ?? 0).toLocaleString()}
            sub={`${year1} full year`}
            change={ordersChange}
            icon={<ShoppingBag size={16} className="text-emerald-600 dark:text-emerald-400" />}
            accent="bg-emerald-50 dark:bg-emerald-900/30"
          />
          <KpiCard
            label="Avg Order Value"
            value={isLoading ? '—' : fmt(summary1?.AvgOrderValue ?? 0)}
            sub={`per order in ${year1}`}
            change={avgChange}
            icon={<Receipt size={16} className="text-violet-600 dark:text-violet-400" />}
            accent="bg-violet-50 dark:bg-violet-900/30"
          />
        </div>

        {/* Monthly Sales Chart */}
        <Card title="Monthly Sales — Year Over Year" icon={<TrendingUp size={16} className="text-indigo-500" />} controls={yearControls}>

          {/* Date range filter */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Date Range:</span>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={e => setEndDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {isFiltered && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Reset
              </button>
            )}
            {!isFiltered && (
              <span className="text-xs text-gray-400 dark:text-gray-500">Showing full year · pick dates for daily view</span>
            )}
          </div>

          {/* KPI tiles */}
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
              <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                {year1}{isFiltered ? ` · ${startDate} – ${endDate}` : ''} Total
              </p>
              <p className="text-base font-bold text-indigo-700 dark:text-indigo-300 mt-0.5">{fmtFull(totals.t1)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                {year2}{isFiltered ? ` · ${startDate.replace(String(year1), String(year2))} – ${endDate.replace(String(year1), String(year2))}` : ''} Total
              </p>
              <p className="text-base font-bold text-gray-700 dark:text-gray-200 mt-0.5">{fmtFull(totals.t2)}</p>
            </div>
            <div className={`rounded-lg p-3 ${totals.diff >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
              <p className={`text-xs font-medium ${totals.diff >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {isFiltered ? 'Period' : 'YoY'} Change
              </p>
              <div className="flex items-center gap-1 mt-0.5">
                {totals.diff >= 0
                  ? <ChevronUp size={18} className="text-emerald-600 dark:text-emerald-400" />
                  : <ChevronDown size={18} className="text-red-600 dark:text-red-400" />}
                <p className={`text-lg font-bold ${totals.diff >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                  {Math.abs(totals.diff).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          {chartLoading ? <Skeleton h="h-72" /> : chartError ? (
            <div className="h-72 flex items-center justify-center text-red-500 text-sm">Failed to load data</div>
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <LineChart data={activeChartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                {isFiltered ? (
                  <XAxis
                    dataKey="label"
                    ticks={dailyTicks}
                    tick={{ fontSize: 11, fill: tickColor }}
                    interval={0}
                  />
                ) : (
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: tickColor }} />
                )}
                <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: tickColor }} width={64} />
                <Tooltip content={<SalesTooltip primaryYear={year1} compareYear={year2} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey={year1} stroke="#6366f1" strokeWidth={2.5} dot={dotRadius > 0 ? { r: dotRadius } : false} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey={year2} stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 3" dot={dotRadius > 0 ? { r: dotRadius } : false} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Top Products */}
        <Card title={`Top Products — ${year1}`} icon={<ShoppingBag size={16} className="text-violet-500" />}>
          {isLoading ? <Skeleton h="h-64" /> : isError ? (
            <div className="h-32 flex items-center justify-center text-red-500 text-sm">Failed to load data</div>
          ) : !data?.topProducts.length ? (
            <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No product data available</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="pb-2 pr-3 font-medium w-7">#</th>
                    <th className="pb-2 pr-4 font-medium">Part Number</th>
                    <th className="pb-2 pr-4 font-medium">Description</th>
                    <th className="pb-2 pr-3 font-medium text-right">Orders</th>
                    <th className="pb-2 font-medium text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {data.topProducts.map((row, i) => (
                    <tr key={row.PartNumber} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="py-2.5 pr-3 text-gray-400 dark:text-gray-500 text-xs tabular-nums">{i + 1}</td>
                      <td className="py-2.5 pr-4 font-mono text-xs text-gray-800 dark:text-gray-200 font-medium">
                        {row.PartNumber}
                      </td>
                      <td className="py-2.5 pr-4 text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {row.FabricName !== row.PartNumber ? row.FabricName : '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                        {row.OrderCount.toLocaleString()}
                      </td>
                      <td className="py-2.5 text-right text-xs font-semibold text-gray-800 dark:text-white tabular-nums">
                        {fmt(row.Revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </MainLayout>
  );
};

export default CustomerReport;
